import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "@/app/types";
import { fetchCourses } from "@/api/fetchCourses";
import { createCourse } from "@/api/createCourse/createCourseApi";
import { updateCourse as updateCourseApi } from "@/api/updateCourse/updateCourseApi";
import { deleteCourse } from "@/api/deleteCourse/deleteCourseApi";
import { publishCourse } from "@/api/publishCourse/publishCourseApi";
import type {
  FetchCoursesParams,
  FetchCoursesResponse,
} from "@/api/fetchCourses/types";
import type { CreateCoursePayload } from "@/api/createCourse/types";
import type {
  UpdateCoursePayload,
  UpdateCourseResponse,
} from "@/api/updateCourse/types";
import type {
  CourseStatus,
  CourseWithDetails,
} from "@/modules/learningCenter/types/course";

/**
 * Course Management State
 *
 * Manages the list of courses with pagination, filtering, and CRUD operations
 */
export interface CourseManagementState {
  courses: CourseWithDetails[];
  totalCount: number;
  loading: boolean;
  error: string | null;
  // Granular loading/error slots for specific operations. The list-level
  // `loading`/`error` above are kept for backwards compatibility with existing
  // selectors; `loading.publish`/`error.publish` mirror the Module Library
  // pattern for the publish action.
  loadingState: {
    publish: boolean;
  };
  errorState: {
    publish: string | null;
  };
  pagination: {
    page: number;
    pageSize: number;
  };
  filters: {
    search?: string;
    tradeId?: number;
    skillId?: number;
    hasQuiz?: boolean;
    includeArchived?: boolean;
    status?: CourseStatus;
  };
  lastFetchParams?: string; // Track last fetch to prevent duplicates
}

// Initial state
const initialState: CourseManagementState = {
  courses: [],
  totalCount: 0,
  loading: false,
  error: null,
  loadingState: {
    publish: false,
  },
  errorState: {
    publish: null,
  },
  pagination: {
    page: 1,
    pageSize: 20,
  },
  filters: {},
};

/**
 * Async thunk: Fetch courses with pagination and filters
 *
 * Includes deduplication to prevent multiple concurrent requests with the same params
 */
export const fetchCoursesThunk = createAsyncThunk<
  FetchCoursesResponse,
  FetchCoursesParams | void,
  { rejectValue: string; state: RootState }
>(
  "courseManagement/fetchCourses",
  async (params, { rejectWithValue }) => {
    try {
      const response = await fetchCourses(params || undefined);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch courses",
      );
    }
  },
  {
    // Prevent duplicate concurrent requests with the same params
    condition: (params, { getState }) => {
      const state = getState();
      const { loading, lastFetchParams } = state.courseManagement || {};

      const paramsKey = JSON.stringify(params || {});

      // Don't fetch if we're already loading the exact same request
      if (loading && lastFetchParams === paramsKey) {
        console.log("[Dedup] Prevented duplicate fetch with params:", params);
        return false; // Cancel this request
      }

      return true; // Allow request to proceed
    },
  },
);

/**
 * Async thunk: Create a new course
 */
export const createCourseThunk = createAsyncThunk<
  CourseWithDetails,
  CreateCoursePayload,
  { rejectValue: string }
>("courseManagement/createCourse", async (payload, { rejectWithValue }) => {
  try {
    const response = await createCourse(payload);
    return response;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to create course",
    );
  }
});

/**
 * Async thunk: Update an existing course
 */
export const updateCourseThunk = createAsyncThunk<
  UpdateCourseResponse,
  { id: number; payload: UpdateCoursePayload },
  { rejectValue: string }
>(
  "courseManagement/updateCourse",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const response = await updateCourseApi(id, payload);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update course",
      );
    }
  },
);

/**
 * Async thunk: Delete a course (soft delete/archive)
 */
export const deleteCourseThunk = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>("courseManagement/deleteCourse", async (id, { rejectWithValue }) => {
  try {
    await deleteCourse(id);
    return id;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to delete course",
    );
  }
});

/**
 * Async thunk: Publish a course (draft -> published)
 *
 * Mirrors the Module Library publish flow. The backend endpoint may 404 until
 * it deploys, in which case the rejection surfaces gracefully to the caller.
 */
export const publishCourseThunk = createAsyncThunk<
  CourseWithDetails,
  number,
  { rejectValue: string }
>("courseManagement/publishCourse", async (id, { rejectWithValue }) => {
  try {
    const response = await publishCourse(id);
    return response.course;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to publish course",
    );
  }
});

/**
 * Course Management Slice
 */
const courseManagementSlice = createSlice({
  name: "courseManagement",
  initialState,
  reducers: {
    setPagination: (
      state,
      action: PayloadAction<{ page?: number; pageSize?: number }>,
    ) => {
      if (action.payload.page !== undefined) {
        state.pagination.page = action.payload.page;
      }
      if (action.payload.pageSize !== undefined) {
        state.pagination.pageSize = action.payload.pageSize;
      }
    },
    setFilters: (
      state,
      action: PayloadAction<CourseManagementState["filters"]>,
    ) => {
      state.filters = action.payload;
      // Reset to first page when filters change
      state.pagination.page = 1;
    },
    clearFilters: (state) => {
      state.filters = {};
      state.pagination.page = 1;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch courses
    builder
      .addCase(fetchCoursesThunk.pending, (state, action) => {
        // Generate a unique key for these params to detect duplicates
        const paramsKey = JSON.stringify(action.meta.arg || {});

        // Skip if we're already loading the exact same request
        if (state.loading && state.lastFetchParams === paramsKey) {
          console.log("[Dedup] Skipping duplicate pending fetch request");
          return;
        }

        state.loading = true;
        state.error = null;
        state.lastFetchParams = paramsKey;
      })
      .addCase(fetchCoursesThunk.fulfilled, (state, action) => {
        state.loading = false;
        console.log("fetchCoursesThunk.fulfilled - payload:", action.payload);
        console.log("courses:", action.payload?.courses);
        state.courses = action.payload?.courses || [];
        state.totalCount = action.payload?.pagination?.totalItems || 0;
      })
      .addCase(fetchCoursesThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch courses";
      });

    // Create course
    builder
      .addCase(createCourseThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCourseThunk.fulfilled, (state, action) => {
        state.loading = false;
        // Add the new course to the beginning of the list
        state.courses.unshift(action.payload);
        state.totalCount += 1;
      })
      .addCase(createCourseThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? "Failed to create course";
      });

    // Update course
    builder
      .addCase(updateCourseThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCourseThunk.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.courses.findIndex(
          (course) => course.id === action.payload.data.id,
        );
        if (index !== -1) {
          state.courses[index] = action.payload.data;
        }
      })
      .addCase(updateCourseThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update course";
      });

    // Delete course
    builder
      .addCase(deleteCourseThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCourseThunk.fulfilled, (state, action) => {
        state.loading = false;
        // Soft-archive in place: keep the course in the list but mark it as
        // archived (mirrors the Module Library archive behavior). This keeps the
        // row visible so the status filter / badge reflect the change.
        const index = state.courses.findIndex(
          (course) => course.id === action.payload,
        );
        if (index !== -1) {
          state.courses[index] = {
            ...state.courses[index],
            status: "archived",
            archived: true,
          };
        }
      })
      .addCase(deleteCourseThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to delete course";
      });

    // Publish course
    builder
      .addCase(publishCourseThunk.pending, (state) => {
        state.loadingState.publish = true;
        state.errorState.publish = null;
      })
      .addCase(publishCourseThunk.fulfilled, (state, action) => {
        state.loadingState.publish = false;
        const index = state.courses.findIndex(
          (course) => course.id === action.payload.id,
        );
        if (index !== -1) {
          state.courses[index] = action.payload;
        }
      })
      .addCase(publishCourseThunk.rejected, (state, action) => {
        state.loadingState.publish = false;
        state.errorState.publish = action.payload || "Failed to publish course";
      });
  },
});

// Actions
export const { setPagination, setFilters, clearFilters, clearError } =
  courseManagementSlice.actions;

// Selectors
export const selectCourses = (state: RootState) =>
  state.courseManagement?.courses || [];
export const selectTotalCount = (state: RootState) =>
  state.courseManagement?.totalCount || 0;
export const selectLoading = (state: RootState) =>
  state.courseManagement?.loading || false;
export const selectError = (state: RootState) =>
  state.courseManagement?.error || null;
export const selectPagination = (state: RootState) =>
  state.courseManagement?.pagination || initialState.pagination;
export const selectFilters = (state: RootState) =>
  state.courseManagement?.filters || {};
export const selectPublishLoading = (state: RootState) =>
  state.courseManagement?.loadingState?.publish || false;
export const selectPublishError = (state: RootState) =>
  state.courseManagement?.errorState?.publish || null;

// Reducer
export default courseManagementSlice.reducer;
